/**
 * Resolves "what should this screen be playing right now?" for the Schedule feature.
 *
 * Deliberately free of database and DOM access: the backend, the CMS calendar and the
 * player all have to reach the same answer, and the player has to reach it while offline.
 *
 * Recurrence is split in two on purpose. RRULE decides *which days* an event lands on
 * and runs in a timezone-free space; the wall-clock window for that day is then built
 * with dayjs in the screen's timezone. Expanding RRULE directly in a zone would shift
 * every window by an hour across a DST boundary.
 */

import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { RRule, type Options } from 'rrule'
import type { TimezoneSource } from './types'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

export const FALLBACK_TIMEZONE = 'UTC'
const DATE_FORMAT = 'YYYY-MM-DD'
const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm'

export type { TimezoneSource }

export interface TimezoneChain {
  screen?: string | null
  device?: string | null
  schedule?: string | null
}

export interface ResolvedTimezone {
  timezone: string
  source: TimezoneSource
}

/** The timing fields the resolver needs. Entities may carry more; this keeps them decoupled. */
export interface ResolvableEvent {
  id: string
  /** Lower wins. Ties break on id so the answer is stable across processes. */
  priority: number
  /** 'YYYY-MM-DD' — first day of the series. */
  startDate: string
  /** 'YYYY-MM-DD' — last day, or null for open-ended. */
  endDate?: string | null
  allDay?: boolean
  /** 'HH:mm' wall clock. Ignored when allDay. */
  startTime?: string
  /** 'HH:mm' wall clock, exclusive. Ignored when allDay. */
  endTime?: string
  /** RFC 5545 RRULE without the DTSTART line, or null for a single day. */
  rrule?: string | null
  /**
   * Days the series skips, as 'YYYY-MM-DD'. Editing one occurrence of a repeating event
   * adds its date here and stores the change as a separate one-off event.
   */
  skipDates?: string[] | null
}

export interface EventWindow<T extends ResolvableEvent = ResolvableEvent> {
  event: T
  start: Dayjs
  end: Dayjs
}

export function isValidTimezone(value?: string | null): value is string {
  if (!value) return false
  try {
    new Intl.DateTimeFormat('en', { timeZone: value })
    return true
  } catch {
    return false
  }
}

/**
 * The screen's own setting wins, then whatever the device reported, then the schedule's
 * default. The source is returned so the UI can say where the value came from instead of
 * showing a timezone the user never chose.
 */
export function resolveTimezone(chain: TimezoneChain): ResolvedTimezone {
  if (isValidTimezone(chain.screen)) return { timezone: chain.screen, source: 'screen' }
  if (isValidTimezone(chain.device)) return { timezone: chain.device, source: 'device' }
  if (isValidTimezone(chain.schedule)) return { timezone: chain.schedule, source: 'schedule' }
  return { timezone: FALLBACK_TIMEZONE, source: 'fallback' }
}

/** A calendar date as a UTC instant, used only as RRULE's timezone-free carrier. */
function floating(date: string): Date {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

function floatingToDate(value: Date): string {
  return value.toISOString().slice(0, 10)
}

/**
 * Both ends are built from a calendar day and a wall clock, never by adding time to an instant.
 * Adding a day to an instant adds 24 real hours, which on a daylight saving night lands the
 * window an hour away from the time it was written to end at.
 */
/**
 * dayjs.tz resolves a wall clock that happens twice (the fall-back hour) to the second, winter-time
 * instant. A window written for 02:30 should start at the first 02:30, so the offset is worked out
 * here: try the offsets in force a day before and a day after, keep the ones that round-trip, and
 * take the earlier instant. A wall clock that does not exist (spring-forward) uses the offset from
 * before the change, which lands it an hour later.
 */
function wallClock(day: string, clock: string, tz: string): Dayjs {
  const [y, mo, d] = day.split('-').map(Number)
  const [h, mi] = clock.split(':').map(Number)
  const local = Date.UTC(y, mo - 1, d, h, mi)
  const offsetAt = (instant: number) => dayjs(instant).tz(tz).utcOffset() * 60_000
  const DAY = 86_400_000
  const before = local - offsetAt(local - DAY)
  const after = local - offsetAt(local + DAY)
  const valid = [before, after].filter((instant) => instant + offsetAt(instant) === local)
  return dayjs(valid.length ? Math.min(...valid) : before).tz(tz)
}

function windowFor<T extends ResolvableEvent>(event: T, date: string, tz: string): EventWindow<T> {
  const at = (day: string, clock: string) => wallClock(day, clock, tz)
  const nextDay = dayjs.tz(`${date} 12:00`, DATETIME_FORMAT, tz).add(1, 'day').format(DATE_FORMAT)

  if (event.allDay) {
    return { event, start: at(date, '00:00'), end: at(nextDay, '00:00') }
  }

  const start = at(date, event.startTime || '00:00')
  const endClock = !event.endTime || event.endTime === '24:00' ? '00:00' : event.endTime
  const sameDayEnd = at(date, endClock)
  // An end at or before the start means the window runs past midnight
  const end = sameDayEnd.isAfter(start) ? sameDayEnd : at(nextDay, endClock)
  return { event, start, end }
}

const EXPANDABLE_FREQUENCIES: number[] = [RRule.YEARLY, RRule.MONTHLY, RRule.WEEKLY, RRule.DAILY]
const MAX_RULE_INTERVAL = 366

/**
 * rrule parses rules it cannot expand in bounded time: a negative interval never terminates
 * and a per-second rule yields 86,400 windows a day. Anything outside day-or-coarser
 * recurrence is refused here, so no caller can hand the expander such a rule.
 */
export function parseRecurrenceRule(rrule: string): Partial<Options> | null {
  let options: Partial<Options>
  try {
    options = RRule.parseString(rrule)
  } catch {
    return null
  }

  if (options.freq === undefined || !EXPANDABLE_FREQUENCIES.includes(options.freq)) return null
  const interval = options.interval ?? 1
  if (!Number.isInteger(interval) || interval < 1 || interval > MAX_RULE_INTERVAL) return null
  if (options.byhour != null || options.byminute != null || options.bysecond != null) return null
  if (options.dtstart || options.tzid) return null
  return options
}

function occurrenceDates(event: ResolvableEvent, from: Dayjs, to: Dayjs, tz: string): string[] {
  const rangeStart = from.tz(tz).format(DATE_FORMAT)
  const rangeEnd = to.tz(tz).format(DATE_FORMAT)
  const seriesEnd = event.endDate || null

  const skipped = new Set(event.skipDates || [])
  const withinSeries = (date: string) =>
    date >= event.startDate && (!seriesEnd || date <= seriesEnd) && !skipped.has(date)

  if (!event.rrule) {
    return withinSeries(event.startDate) && event.startDate >= rangeStart && event.startDate <= rangeEnd
      ? [event.startDate]
      : []
  }

  const options = parseRecurrenceRule(event.rrule)
  if (!options) return []

  let rule: RRule
  try {
    rule = new RRule({ ...options, dtstart: floating(event.startDate) })
  } catch {
    return []
  }

  return rule.between(floating(rangeStart), floating(rangeEnd), true).map(floatingToDate).filter(withinSeries)
}

/**
 * Every window of `event` that overlaps [from, to). The range is widened by a day on each
 * side first, because a window that starts the previous evening can still be running.
 */
export function expandOccurrences<T extends ResolvableEvent>(
  event: T,
  from: Dayjs,
  to: Dayjs,
  tz: string
): Array<EventWindow<T>> {
  const dates = occurrenceDates(event, from.subtract(1, 'day'), to.add(1, 'day'), tz)
  return dates
    .map((date) => windowFor(event, date, tz))
    .filter((window) => window.end.isAfter(from) && window.start.isBefore(to))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf())
}

function byPriority(a: EventWindow, b: EventWindow): number {
  return a.event.priority - b.event.priority || a.event.id.localeCompare(b.event.id)
}

/**
 * The winning event at `instant`, or null when nothing is active and the caller should
 * fall back. Window start is inclusive, end exclusive, so a window ending at 14:00 and
 * one starting at 14:00 never both match.
 */
export function resolveAt<T extends ResolvableEvent>(events: T[], instant: Dayjs, tz: string): EventWindow<T> | null {
  const active: Array<EventWindow<T>> = []

  for (const event of events) {
    for (const window of expandOccurrences(event, instant, instant.add(1, 'millisecond'), tz)) {
      if (!window.start.isAfter(instant) && window.end.isAfter(instant)) active.push(window)
    }
  }

  if (!active.length) return null
  return active.sort(byPriority)[0]
}

/**
 * When the answer from `resolveAt` could next change. The player schedules a single timer
 * on this instead of re-resolving every minute; null means nothing changes within `horizon`.
 */
export function nextBoundaryAfter(
  events: ResolvableEvent[],
  instant: Dayjs,
  tz: string,
  horizonDays = 14
): Dayjs | null {
  const horizon = instant.add(horizonDays, 'day')
  let earliest: Dayjs | null = null

  for (const event of events) {
    for (const window of expandOccurrences(event, instant, horizon, tz)) {
      for (const edge of [window.start, window.end]) {
        if (edge.isAfter(instant) && (!earliest || edge.isBefore(earliest))) earliest = edge
      }
    }
  }

  return earliest
}

/** The fields that decide which of two overlapping events is the more specific one. */
export interface SpecificityInput {
  rrule?: string | null
  allDay?: boolean
  startTime?: string
  endTime?: string
}

/** How many days a year the rule fires. Fewer days is more specific. */
export function daysPerYear(event: Pick<SpecificityInput, 'rrule'>): number {
  const rule = event.rrule || ''
  if (!rule) return 1
  if (rule.includes('FREQ=YEARLY')) return 1
  if (rule.includes('FREQ=MONTHLY')) return 12
  if (rule.includes('FREQ=DAILY')) return 365
  if (rule.includes('FREQ=WEEKLY')) {
    const byDay = rule.match(/BYDAY=([A-Z,]+)/)?.[1]
    return byDay ? 52 * byDay.split(',').length : 52
  }
  return 365
}

function windowMinutes(event: SpecificityInput): number {
  if (event.allDay) return 24 * 60
  const [sh, sm] = (event.startTime || '00:00').split(':').map(Number)
  const [eh, em] = (event.endTime || '23:59').split(':').map(Number)
  const minutes = eh * 60 + em - (sh * 60 + sm)
  return minutes > 0 ? minutes : minutes + 24 * 60
}

/** Negative when `a` should beat `b`: the exception beats the rule, then the shorter window. */
export function bySpecificity(a: SpecificityInput, b: SpecificityInput): number {
  return daysPerYear(a) - daysPerYear(b) || windowMinutes(a) - windowMinutes(b)
}

/**
 * Where a new event lands in a priority-ordered list when nobody has chosen: above the first
 * event it is more specific than. The server places with this and the editor previews with it.
 */
export function placementIndex(ordered: SpecificityInput[], candidate: SpecificityInput): number {
  const index = ordered.findIndex((event) => bySpecificity(candidate, event) < 0)
  return index === -1 ? ordered.length : index
}

export interface TimeRange {
  startMs: number
  endMs: number
}

/**
 * The parts of `window` that `others` cover, merged and in time order. An event is hidden
 * exactly while some higher event is running, so this is what separates "loses for an hour"
 * from "never plays".
 */
export function coveredRanges(window: TimeRange, others: TimeRange[]): TimeRange[] {
  const clipped = others
    .map((other) => ({
      startMs: Math.max(other.startMs, window.startMs),
      endMs: Math.min(other.endMs, window.endMs),
    }))
    .filter((range) => range.endMs > range.startMs)
    .sort((a, b) => a.startMs - b.startMs)

  const merged: TimeRange[] = []
  for (const range of clipped) {
    const last = merged[merged.length - 1]
    if (last && range.startMs <= last.endMs) last.endMs = Math.max(last.endMs, range.endMs)
    else merged.push(range)
  }
  return merged
}

export function coversWhole(window: TimeRange, covered: TimeRange[]): boolean {
  return covered.length === 1 && covered[0].startMs <= window.startMs && covered[0].endMs >= window.endMs
}
